/**
 * Interpolation proposal card (the runtime HITL gate). Interpolation edits
 * the structure IN PLACE, like the Interpolation operation in the
 * right-hand panel. Pure presentational: it shows the RESOLVED target and
 * exactly what will be filled — for "all empty", how many empty slices lie
 * between drawn slices; for the current slice, its position (captured when
 * the card was built; Apply fills that slice even if the view has moved)
 * and whether it is interpolated or copied from one neighbour. There is no
 * geometry preview. Frame, status chip and Apply/Reject live in
 * {@link ProposalCardShell}.
 */
import { Typography } from '@mui/material';
import type { InterpolateProposal } from '~/agent';
import { formatSignedCm, mmToCm } from '~/utils/units';
import ProposalCardShell, {
    ProposalSummaryBox,
    TargetLine,
    type ProposalStatus,
} from './ProposalCardShell';

export interface InterpolateProposalCardProps {
    readonly targetName: string;
    readonly targetRoiNumber: number;
    readonly currentVolumeCm3: number;
    readonly scope: InterpolateProposal['scope'];
    readonly slicesToFill?: number;
    /** Set for the current-slice scope (mm, patient z). */
    readonly slicePositionMm?: number;
    readonly currentSliceFill?: InterpolateProposal['currentSliceFill'];
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

function describe(
    p: Pick<
        InterpolateProposalCardProps,
        'scope' | 'slicesToFill' | 'slicePositionMm' | 'currentSliceFill'
    >
): string {
    if (p.scope === 'all-empty') {
        const n = p.slicesToFill ?? 0;
        return `fill ${n} empty slice${n === 1 ? '' : 's'} between drawn slices`;
    }
    const z = formatSignedCm(mmToCm(p.slicePositionMm ?? Number.NaN));
    switch (p.currentSliceFill) {
        case 'copy-below':
            return `copy the nearest drawn slice below onto the current slice (z = ${z})`;
        case 'copy-above':
            return `copy the nearest drawn slice above onto the current slice (z = ${z})`;
        default:
            return `interpolate the current slice (z = ${z}) from its drawn neighbours`;
    }
}

export default function InterpolateProposalCard({
    targetName,
    targetRoiNumber,
    currentVolumeCm3,
    scope,
    slicesToFill,
    slicePositionMm,
    currentSliceFill,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: InterpolateProposalCardProps) {
    return (
        <ProposalCardShell
            title="Interpolate slices"
            ariaLabel="Interpolate slices proposal"
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            <TargetLine
                targetName={targetName}
                targetRoiNumber={targetRoiNumber}
                currentVolumeCm3={currentVolumeCm3}
            />

            <ProposalSummaryBox>
                {describe({
                    scope,
                    slicesToFill,
                    slicePositionMm,
                    currentSliceFill,
                })}{' '}
                of “{targetName}”
            </ProposalSummaryBox>

            <Typography variant="caption" color="text.secondary">
                This edits “{targetName}” itself, like the Interpolation
                operation; no new structure is created. Undo restores it.
            </Typography>
        </ProposalCardShell>
    );
}
